import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Dialog } from "@mui/material";
import LocationOffOutlinedIcon from "@mui/icons-material/LocationOffOutlined";
import { GoogleMap, OverlayView, OverlayViewF, PolylineF, useJsApiLoader } from "@react-google-maps/api";
import { dt } from "../../../../shared/themes/redesignTokens";
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID } from "../../../../shared/config/googleMapsConfig";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { computeDistanceKm } from "../../../../shared/utils/geoUtils";
import HomeMarker from "../../../serviceBoard/components/map/HomeMarker";
import CitySidebar from "../../../serviceBoard/components/map/CitySidebar";
import publicServiceRequestsApi from "../../../serviceBoard/api/publicServiceRequestsApi";
import { MapLocation } from "../../../serviceBoard/models/models";
import RfsOwnerMarker, { getPriorityColor } from "./RfsOwnerMarker";

// ===================================================================
//  TYPES
// ===================================================================

export interface RfsMapData {
  ownerLat: number;
  ownerLng: number;
  ownerName: string;
  ownerLogoUrl?: string;
  ownerCity?: string;
  priorityTypeCode: number;
  internalNumber: string;
  ownerCustomerId?: string;
  serviceRequestId?: string;
}

export interface ProviderLocation {
  lat: number;
  lng: number;
}

interface RfsMapModalProps {
  open: boolean;
  onClose: () => void;
  mapData: RfsMapData;
  providerLocation: ProviderLocation | null;
}

// ===================================================================
//  KEYFRAMES (injected once)
// ===================================================================

const DIALOG_KEYFRAMES_ID = "rfs-dialog-enter-kf";
const injectDialogKeyframes = () => {
  if (document.getElementById(DIALOG_KEYFRAMES_ID)) return;
  const style = document.createElement("style");
  style.id = DIALOG_KEYFRAMES_ID;
  style.textContent = `
    @keyframes rfsDialogEnter {
      0%   { opacity: 0; transform: scale(0.96) translateY(8px); }
      100% { opacity: 1; transform: scale(1) translateY(0); }
    }
    @keyframes rfsDistanceFadeIn {
      0%   { opacity: 0; transform: translateY(4px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    @keyframes rfsMarkerPulse {
      0%   { transform: scale(1); opacity: 0.7; }
      50%  { transform: scale(1.4); opacity: 0; }
      100% { transform: scale(1); opacity: 0; }
    }
  `;
  document.head.appendChild(style);
};

// ===================================================================
//  MAP STYLES (hide POIs, transit)
// ===================================================================

const mapStyles: google.maps.MapTypeStyle[] = [
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
];

// ===================================================================
//  DISTANCE LABEL (frosted pill at midpoint)
// ===================================================================

interface DistanceLabelProps {
  position: { lat: number; lng: number };
  distanceKm: number;
}

const DistanceLabel: React.FC<DistanceLabelProps> = React.memo(({ position, distanceKm }) => {
  const pos = useMemo(
    () => ({ lat: position.lat, lng: position.lng }),
    [position.lat, position.lng]
  );

  const getPixelPositionOffset = useMemo(
    () => () => ({ x: -40, y: -20 }),
    []
  );

  const formattedDistance = distanceKm < 1
    ? `${Math.round(distanceKm * 1000)} m`
    : `${distanceKm.toFixed(1)} km`;

  return (
    <OverlayViewF
      position={pos}
      mapPaneName={OverlayView.FLOAT_PANE}
      getPixelPositionOffset={getPixelPositionOffset}
    >
      <div
        style={{
          backgroundColor: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(10px)",
          borderRadius: "20px",
          padding: "6px 16px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.6) inset",
          border: "1px solid rgba(0,0,0,0.06)",
          pointerEvents: "none",
          animation: "rfsDistanceFadeIn 0.3s ease 0.3s both",
        }}
      >
        <span
          style={{
            fontFamily: dt.fontMono,
            fontSize: "13px",
            fontWeight: 600,
            color: dt.textPrimary,
            whiteSpace: "nowrap",
          }}
        >
          {formattedDistance}
        </span>
      </div>
    </OverlayViewF>
  );
});

// ===================================================================
//  MAIN COMPONENT
// ===================================================================

const RfsMapModal: React.FC<RfsMapModalProps> = ({
  open,
  onClose,
  mapData,
  providerLocation,
}) => {
  const { translate } = useTranslation(["servicing", "serviceBoard"]);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);

  // Sidebar state
  const [companyLocation, setCompanyLocation] = useState<MapLocation | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [cachedLocation, setCachedLocation] = useState<MapLocation | null>(null);

  const apiKey = (window as any).app?.config?.googleMapsApiKey || "";
  const { isLoaded } = useJsApiLoader({ id: GOOGLE_MAPS_SCRIPT_ID, googleMapsApiKey: apiKey, libraries: GOOGLE_MAPS_LIBRARIES });

  const priorityColor = getPriorityColor(mapData.priorityTypeCode);

  const ownerPosition = useMemo(
    () => ({ lat: mapData.ownerLat, lng: mapData.ownerLng }),
    [mapData.ownerLat, mapData.ownerLng]
  );

  const hasBothLocations = providerLocation != null;

  // Distance between the two markers
  const distanceKm = useMemo(() => {
    if (!providerLocation) return null;
    return computeDistanceKm(
      mapData.ownerLat,
      mapData.ownerLng,
      providerLocation.lat,
      providerLocation.lng
    );
  }, [mapData.ownerLat, mapData.ownerLng, providerLocation]);

  // Midpoint between the two markers for the distance label
  const midpoint = useMemo(() => {
    if (!providerLocation) return null;
    return {
      lat: (mapData.ownerLat + providerLocation.lat) / 2,
      lng: (mapData.ownerLng + providerLocation.lng) / 2,
    };
  }, [mapData.ownerLat, mapData.ownerLng, providerLocation]);

  // Show on-map distance label only if >= 1km
  const showOnMapDistance = distanceKm != null && distanceKm >= 1;

  // Polyline path
  const polylinePath = useMemo(() => {
    if (!providerLocation) return null;
    return [ownerPosition, providerLocation];
  }, [ownerPosition, providerLocation]);

  // Distance text for header
  const headerDistanceText = useMemo(() => {
    if (distanceKm == null) return null;
    if (distanceKm < 1) return `${Math.round(distanceKm * 1000)} m`;
    return `${distanceKm.toFixed(1)} km`;
  }, [distanceKm]);

  // Inject keyframes
  useEffect(() => {
    injectDialogKeyframes();
  }, []);

  const handleMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;

      if (hasBothLocations && providerLocation) {
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(ownerPosition);
        bounds.extend(providerLocation);
        map.fitBounds(bounds, { top: 60, bottom: 60, left: 60, right: 60 });
      } else {
        map.setCenter(ownerPosition);
        map.setZoom(14);
      }

      setTimeout(() => setMapReady(true), 100);
    },
    [hasBothLocations, providerLocation, ownerPosition]
  );

  // Handle marker click -> toggle sidebar
  const handleMarkerClick = useCallback(async () => {
    if (sidebarOpen) {
      // Toggle off
      setSidebarOpen(false);
      setCompanyLocation(null);
      return;
    }

    // If cached, reuse
    if (cachedLocation) {
      setCompanyLocation(cachedLocation);
      setSidebarOpen(true);
      return;
    }

    // Fetch company's RFS from map endpoint
    if (!mapData.ownerCustomerId) return;

    setSidebarLoading(true);
    setSidebarOpen(true);

    try {
      const result = await publicServiceRequestsApi.getMapDataAsync({
        ownerCustomerId: mapData.ownerCustomerId,
      });

      if (result.locations && result.locations.length > 0) {
        // Pick ONLY the location matching the owner's city/coordinates
        let matchedLocation: MapLocation | null = null;

        // First try exact city match
        if (mapData.ownerCity) {
          matchedLocation = result.locations.find(
            (loc) => loc.city === mapData.ownerCity
          ) || null;
        }

        // Fallback: closest lat/lng match
        if (!matchedLocation) {
          let closestDist = Infinity;
          for (const loc of result.locations) {
            const dist = computeDistanceKm(
              mapData.ownerLat,
              mapData.ownerLng,
              loc.latitude,
              loc.longitude
            );
            if (dist < closestDist) {
              closestDist = dist;
              matchedLocation = loc;
            }
          }
        }

        if (matchedLocation) {
          setCompanyLocation(matchedLocation);
          setCachedLocation(matchedLocation);
        }
      }
    } catch (err) {
      console.error("Failed to load company RFS for sidebar:", err);
    } finally {
      setSidebarLoading(false);
    }
  }, [sidebarOpen, cachedLocation, mapData.ownerCustomerId, mapData.ownerCity, mapData.ownerLat, mapData.ownerLng]);

  // After sidebar transition, resize map and re-fit bounds
  useEffect(() => {
    if (!mapRef.current) return;
    const timer = setTimeout(() => {
      if (mapRef.current) {
        google.maps.event.trigger(mapRef.current, "resize");
        // Re-fit bounds
        if (hasBothLocations && providerLocation) {
          const bounds = new google.maps.LatLngBounds();
          bounds.extend(ownerPosition);
          bounds.extend(providerLocation);
          mapRef.current.fitBounds(bounds, {
            top: 60,
            bottom: 60,
            left: 60,
            right: sidebarOpen ? 420 : 60,
          });
        }
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [sidebarOpen, hasBothLocations, providerLocation, ownerPosition]);

  // Handle sidebar close
  const handleSidebarClose = useCallback(() => {
    setSidebarOpen(false);
    setCompanyLocation(null);
  }, []);

  // Reset state when dialog closes
  const handleClose = useCallback(() => {
    setMapReady(false);
    setSidebarOpen(false);
    setCompanyLocation(null);
    setSidebarLoading(false);
    onClose();
  }, [onClose]);

  // Handle "currently viewing" click on the card that matches this RFS
  const handleCurrentRequestClick = useCallback(() => {
    // Close the sidebar — user is already on this page
    handleSidebarClose();
  }, [handleSidebarClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 24px 80px rgba(0,0,0,0.12), 0 8px 24px rgba(0,0,0,0.06)",
          border: "1px solid rgba(255,255,255,0.8)",
          animation: "rfsDialogEnter 0.3s cubic-bezier(0.16,1,0.3,1) both",
          width: sidebarOpen ? "1200px" : "900px",
          maxWidth: "95vw",
          transition: "width 350ms ease",
        },
      }}
    >
      {/* Priority accent strip */}
      <Box
        sx={{
          height: "4px",
          width: "100%",
          background: `linear-gradient(90deg, ${priorityColor}, ${priorityColor}80)`,
          borderRadius: "20px 20px 0 0",
          flexShrink: 0,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "20px 24px 16px 24px",
          background: `linear-gradient(135deg, ${priorityColor}08 0%, transparent 60%)`,
          borderBottom: `1px solid ${dt.cardBorder}`,
          flexShrink: 0,
        }}
      >
        {/* Left: company name + city/distance */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, minWidth: 0, flex: 1, pr: 8 }}>
          <Box
            component="span"
            sx={{
              fontFamily: dt.font,
              fontSize: "18px",
              fontWeight: 700,
              color: dt.textPrimary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {mapData.ownerName}
          </Box>

          {/* City + distance line */}
          {(mapData.ownerCity || headerDistanceText) && (
            <Box
              component="span"
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "13px",
                fontWeight: 600,
                color: dt.blue,
              }}
            >
              {mapData.ownerCity || ""}
              {mapData.ownerCity && headerDistanceText ? " \u00B7 " : ""}
              {headerDistanceText || ""}
            </Box>
          )}
        </Box>

        {/* RFS number - absolute positioned */}
        <Box
          component="span"
          sx={{
            position: "absolute",
            top: "20px",
            right: "56px",
            fontFamily: dt.fontMono,
            fontSize: "12px",
            fontWeight: 500,
            color: dt.textMuted,
          }}
        >
          {mapData.internalNumber}
        </Box>

        {/* Close hint (subtle circle) */}
        <Box
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: "12px",
            right: "12px",
            width: 28,
            height: 28,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            opacity: 0.35,
            transition: "opacity 0.2s ease, background-color 0.2s ease",
            fontSize: "16px",
            color: dt.textPrimary,
            fontWeight: 300,
            userSelect: "none",
            "&:hover": {
              opacity: 0.8,
              backgroundColor: "rgba(0,0,0,0.04)",
            },
          }}
        >
          {"\u00D7"}
        </Box>
      </Box>

      {/* Map area */}
      <Box sx={{ width: "100%", height: 520, position: "relative" }}>
        {isLoaded ? (
          <>
            {/* Top gradient overlay */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "40px",
                background: "linear-gradient(180deg, rgba(255,255,255,0.5) 0%, transparent 100%)",
                pointerEvents: "none",
                zIndex: 1,
              }}
            />

            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={ownerPosition}
              zoom={14}
              onLoad={handleMapLoad}
              options={{
                styles: mapStyles,
                disableDefaultUI: false,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                gestureHandling: "greedy",
              }}
            >
              {/* RFS Owner Marker (clickable when ownerCustomerId available) */}
              <RfsOwnerMarker
                position={ownerPosition}
                companyName={mapData.ownerName}
                logoUrl={mapData.ownerLogoUrl}
                priorityCode={mapData.priorityTypeCode}
                onClick={mapData.ownerCustomerId ? handleMarkerClick : undefined}
                isSelected={sidebarOpen}
              />

              {/* Home (provider) Marker */}
              {providerLocation && (
                <HomeMarker position={providerLocation} />
              )}

              {/* Distance line */}
              {polylinePath && (
                <PolylineF
                  path={polylinePath}
                  options={{
                    strokeColor: "#3498DB",
                    strokeOpacity: 0.5,
                    strokeWeight: 2,
                    geodesic: true,
                  }}
                />
              )}

              {/* Distance label (frosted pill at midpoint) */}
              {mapReady && showOnMapDistance && midpoint && distanceKm != null && (
                <DistanceLabel position={midpoint} distanceKm={distanceKm} />
              )}
            </GoogleMap>

            {/* Loading panel while fetching sidebar data */}
            {sidebarLoading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: "380px",
                  maxWidth: "100%",
                  backgroundColor: "rgba(255,255,255,0.85)",
                  backdropFilter: "blur(8px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 20,
                  borderLeft: `1px solid ${dt.cardBorder}`,
                }}
              >
                <CircularProgress size={32} sx={{ color: dt.blue }} />
              </Box>
            )}

            {/* CitySidebar (overlays right side of map) */}
            {!sidebarLoading && (
              <CitySidebar
                location={sidebarOpen ? companyLocation : null}
                onClose={handleSidebarClose}
                mode="side"
                currentRequestId={mapData.serviceRequestId}
                onCurrentRequestClick={handleCurrentRequestClick}
              />
            )}
          </>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: dt.inputBg,
            }}
          >
            <Box sx={{ fontSize: "13px", fontFamily: dt.font, color: dt.textMuted }}>
              {translate("serviceBoard:mapLoading")}
            </Box>
          </Box>
        )}

        {/* No-location fallback chip */}
        {!providerLocation && (
          <Box
            sx={{
              position: "absolute",
              bottom: 16,
              left: 16,
              display: "flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: dt.blueGlow,
              borderRadius: "20px",
              padding: "6px 14px",
              zIndex: 10,
            }}
          >
            <LocationOffOutlinedIcon sx={{ fontSize: 16, color: dt.blue }} />
            <Box
              component="span"
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                fontWeight: 500,
                color: dt.blue,
              }}
            >
              {translate("servicing:rfsDetail.setLocationHint")}
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default RfsMapModal;
